import { SearchableSelect } from "@/components/ui/searchable-select";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SearchSelect } from "@/components/ui/search-select";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { api, unwrapObject } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { toQuery } from "@/lib/filters";
import { formatINRCompact } from "@/lib/utils";
import { initialsOf } from "@/components/patterns";
import { WhatsAppButton } from "@/components/whatsapp/WhatsAppButton";
import { Phone } from "lucide-react";
import type { DealRecord } from "@/types";
import { CallLink } from "@/components/calling/CallLink";
import type { DealSummary } from "./types";

interface TeamMember {
  contact_id: string;
  name: string;
  job_title: string | null;
  phone?: string | null;
  role: string | null;
  role_label: string;
  is_primary: boolean;
}

const ROLE_TONE: Record<string, string> = {
  DECISION_MAKER: "bg-sidebar text-white",
  ECONOMIC_BUYER: "bg-sidebar text-white",
  CHAMPION: "bg-gold-soft text-gold-ink",
  INFLUENCER: "bg-accent text-accent-foreground",
  BLOCKER: "bg-danger-soft text-danger",
};

const Card = ({ title, aside, children }: { title: string; aside?: React.ReactNode; children: React.ReactNode }) => (
  <section className="flex flex-col gap-2 rounded-lg border border-border bg-card px-3.5 py-3">
    <div className="flex items-center justify-between">
      <h2 className="text-[11.5px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</h2>
      {aside}
    </div>
    {children}
  </section>
);

/** Account figures, the customer's buying team, and this deal's proposals and quotes */
export function DealRelated({ deal, summary, canWrite, productLines = 0, onOpenProducts }: { deal: DealRecord; summary: DealSummary | null; canWrite: boolean; productLines?: number; onOpenProducts?: () => void }) {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { hasPermission } = useAuth();
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [roles, setRoles] = useState<Record<string, string>>({});
  const [docs, setDocs] = useState<{ key: string; label: string; badge: string; tone: string; to: string }[]>([]);
  const [dialog, setDialog] = useState<{ contactId: string; label: string; role: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const loadTeam = () =>
    api
      .get(`/api/DealContact/List/${deal.id}`)
      .then((r) => {
        const d = unwrapObject<{ team: TeamMember[]; roles: Record<string, string> }>(r);
        setTeam(d.team || []);
        setRoles(d.roles || {});
      })
      .catch(() => undefined);

  useEffect(() => {
    loadTeam();
    const rows: typeof docs = [];
    Promise.all([
      hasPermission("proposal.read") ? api.get(`/api/Proposal/List${toQuery({ deal_id: deal.id, page_size: 20 })}`).then((r) => unwrapPage<any>(r).rows).catch(() => []) : [],
      hasPermission("quote.read") ? api.get(`/api/Quotation/List${toQuery({ deal_id: deal.id, page_size: 20 })}`).then((r) => unwrapPage<any>(r).rows).catch(() => []) : [],
    ]).then(([proposals, quotes]) => {
      for (const p of proposals as any[]) {
        if (p.proposal_status === "SUPERSEDED") continue;
        const viewed = p.view_count > 0;
        rows.push({ key: p.id, label: `${p.display_number}${p.version > 1 ? ` v${p.version}` : ""}`, badge: viewed ? `Viewed ${p.view_count}×` : p.proposal_status.replace(/_/g, " "), tone: viewed ? "bg-info-soft text-info" : "bg-muted text-foreground", to: `/proposals/${p.id}` });
      }
      for (const q of quotes as any[]) {
        if (q.quote_status === "SUPERSEDED") continue;
        const good = ["APPROVED", "ACCEPTED"].includes(q.quote_status);
        rows.push({ key: q.id, label: q.display_number, badge: q.quote_status.replace(/_/g, " "), tone: good ? "bg-success-soft text-success" : q.quote_status === "REJECTED" ? "bg-danger-soft text-danger" : "bg-muted text-foreground", to: `/quotes/${q.id}` });
      }
      setDocs(rows);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal.id]);

  const removeMember = async () => {
    if (!dialog?.contactId || !dialog.label) return;
    setSaving(true);
    try {
      await api.delete(`/api/DealContact/Remove/${deal.id}/${dialog.contactId}`);
      success("Taken off the buying team", dialog.label);
      setDialog(null);
      loadTeam();
    } catch (e: any) {
      error("Could not remove", e.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (!dialog?.contactId || !dialog.role) return;
    setSaving(true);
    try {
      await api.put(`/api/DealContact/Upsert/${deal.id}`, { contact_id: dialog.contactId, role: dialog.role });
      success("Buying team updated", dialog.label);
      setDialog(null);
      loadTeam();
    } catch (e: any) {
      error("Could not save", e.message || "Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const acc = summary?.account;

  return (
    <div className="flex flex-col gap-3">
      <Card
        title="Money"
        aside={
          onOpenProducts && (
            <button type="button" onClick={onOpenProducts} className="text-xs text-primary hover:underline">
              Open products
            </button>
          )
        }
      >
        <div className="flex items-center justify-between gap-2 border-t border-border/60 pt-1.5 first:border-t-0 first:pt-0">
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="text-[13px] font-medium">Products</span>
            <span className="text-[11.5px] text-muted-foreground">{productLines > 0 ? `${productLines} ${productLines === 1 ? "line" : "lines"}` : "None added yet"}</span>
          </span>
          <span className="font-mono text-[13px] font-semibold">{formatINRCompact(deal.amount)}</span>
        </div>
        {docs.length === 0 ? (
          <span className="border-t border-border/60 pt-1.5 text-xs text-muted-foreground">No proposal or quote yet.</span>
        ) : (
          docs.map((d) => (
            <button key={d.key} type="button" onClick={() => navigate(d.to)} className="flex items-center justify-between gap-2 border-t border-border/60 pt-1.5 text-left hover:bg-muted">
              <span className="flex min-w-0 flex-col leading-tight">
                <span className="font-mono text-xs font-semibold">{d.label}</span>
              </span>
              <span className={`rounded px-1.5 text-[11px] font-semibold leading-[18px] ${d.tone}`}>{d.badge}</span>
            </button>
          ))
        )}
      </Card>

      <Card
        title="Buying team"
        aside={
          canWrite && (
            <button type="button" onClick={() => setDialog({ contactId: "", label: "", role: "" })} className="text-xs text-primary hover:underline">
              + Add
            </button>
          )
        }
      >
        {team.length === 0 ? (
          <span className="text-xs text-muted-foreground">No one added yet.</span>
        ) : (
          team.map((m) => (
            <div key={m.contact_id} className="flex items-center gap-1.5">
              <button
                type="button"
                disabled={!canWrite}
                onClick={() => setDialog({ contactId: m.contact_id, label: m.name, role: m.role || "" })}
                className="flex min-w-0 flex-1 items-center gap-2 rounded-md text-left enabled:hover:bg-muted"
              >
                <span className="flex size-[26px] shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-bold">{initialsOf(m.name)}</span>
                <span className="flex min-w-0 flex-1 flex-col leading-tight">
                  <span className="truncate text-[13px] font-medium">{m.name}</span>
                  <span className="truncate text-[11.5px] text-muted-foreground">{m.job_title || (m.is_primary ? "Primary contact" : "")}</span>
                </span>
                <span className={`whitespace-nowrap rounded px-1.5 text-[10.5px] font-semibold leading-[18px] ${m.role ? ROLE_TONE[m.role] || "bg-muted text-foreground" : "bg-muted text-muted-foreground"}`}>{m.role ? m.role_label.replace(/ \(.*\)/, "") : "No role"}</span>
              </button>
              {m.phone && (
                <span className="flex shrink-0 items-center">
                  <CallLink type="CONTACT" id={m.contact_id} number={m.phone} title={`Call ${m.name}`} aria-label={`Call ${m.name}`} className="inline-flex size-6 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground">
                    <Phone className="size-3.5" />
                  </CallLink>
                  <WhatsAppButton phone={m.phone} name={m.name} contactId={m.contact_id} dealId={deal.id} />
                </span>
              )}
            </div>
          ))
        )}
      </Card>

      <Card title="Account">
        {acc ? (
          <>
            <Link to={`/accounts/${acc.id}`} className="text-[13px] font-semibold text-primary hover:underline">
              {acc.name}
            </Link>
            <span className="text-xs text-muted-foreground">
              {[acc.state, `customer since ${new Date(acc.since).getFullYear()}`].filter(Boolean).join(" · ")}
            </span>
            <div className="flex gap-2">
              <div className="flex-1 rounded-md bg-gold-soft px-2 py-1.5">
                <div className="text-[11px] text-gold-ink">Lifetime won</div>
                <div className="font-mono text-[13px] font-semibold">{formatINRCompact(acc.lifetime_won)}</div>
              </div>
              <div className="flex-1 rounded-md bg-panel px-2 py-1.5">
                <div className="text-[11px] text-muted-foreground">Open</div>
                <div className="font-mono text-[13px] font-semibold">
                  {acc.open_deals} {acc.open_deals === 1 ? "deal" : "deals"}
                </div>
              </div>
            </div>
          </>
        ) : (
          <span className="text-xs text-muted-foreground">No account linked.</span>
        )}
      </Card>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{dialog?.label ? `Role of ${dialog.label}` : "Add to buying team"}</DialogTitle>
            <DialogDescription>Who at the customer is involved in this deal, and what part they play.</DialogDescription>
          </DialogHeader>
          {dialog && !dialog.label && (
            <SearchSelect
              value={dialog.contactId}
              endpoint="/api/Contact/List"
              extraParams={{ account_id: deal.account_id }}
              placeholder="Search the account's contacts"
              getLabel={(c) => `${c.first_name} ${c.last_name || ""}`.trim()}
              onChange={(id, row) => setDialog((d) => d && { ...d, contactId: id, label: row ? `${row.first_name} ${row.last_name || ""}`.trim() : "" })}
            />
          )}
          <SearchableSelect value={dialog?.role || ""} onChange={(e) => setDialog((d) => d && { ...d, role: e.target.value })} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm">
            <option value="">Choose a role</option>
            {Object.entries(roles).map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </SearchableSelect>
          <DialogFooter>
            {dialog?.label && team.some((m) => m.contact_id === dialog.contactId && m.role) && (
              <Button variant="outline" className="mr-auto text-danger hover:text-danger" disabled={saving} onClick={removeMember}>
                Remove from team
              </Button>
            )}
            <Button variant="outline" onClick={() => setDialog(null)}>
              Cancel
            </Button>
            <Button disabled={!dialog?.contactId || !dialog.role || saving} onClick={save}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
